import React, { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import EditIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import { plural } from './comum'

interface Grupo { id: number; nome: string; ordem: number; dashboards: unknown[] }

/** Os grupos do menu de um ambiente: criar, renomear, mudar a ordem e excluir (só vazio). */
export default function GruposDoMenu({ ambienteId, grupos, aoMudar }: { ambienteId: number; grupos: Grupo[]; aoMudar: () => void }) {
  const { notify } = useNotification()
  const [janela, setJanela] = useState<{ tipo: 'novo' | 'renomear' | 'excluir'; grupo?: Grupo } | null>(null)
  const [nome, setNome] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const ordenados = [...grupos].sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome))

  const abrir = (tipo: 'novo' | 'renomear' | 'excluir', grupo?: Grupo) => { setNome(grupo?.nome || ''); setErro(null); setJanela({ tipo, grupo }) }

  const mover = async (i: number, para: number) => {
    const ids = ordenados.map(g => g.id)
    ;[ids[i], ids[para]] = [ids[para], ids[i]]
    const r = await services.api.setGroupOrder(ids)
    if (r.fail) { notify(r.error || 'Não foi possível mudar a ordem.', { variant: 'error' }); return }
    aoMudar()
  }

  const confirmar = async () => {
    if (!janela) return
    setSalvando(true); setErro(null)
    const r = janela.tipo === 'novo' ? await services.api.addEnvGroup(nome.trim(), ambienteId)
      : janela.tipo === 'renomear' ? await services.api.updateEnvGroup(nome.trim(), janela.grupo!.id)
        : await services.api.deleteEnvGroup(janela.grupo!.id)
    setSalvando(false)
    if (r.fail) { setErro(r.error || 'Não foi possível salvar.'); return }
    // Grupo novo entra no fim do menu.
    if (janela.tipo === 'novo' && r.data?.id) await services.api.setGroupOrder([...ordenados.map(g => g.id), r.data.id])
    notify(janela.tipo === 'novo' ? 'Grupo criado no fim do menu.' : janela.tipo === 'renomear' ? 'Grupo renomeado.' : 'Grupo excluído.', { variant: 'success' })
    setJanela(null)
    aoMudar()
  }

  return (
    <div className="ng-cartao">
      <div className="ng-cartao-cab">
        <h2>Grupos do menu</h2>
        <div className="ng-acoes"><button type="button" className="ng-btn" onClick={() => abrir('novo')}><AddIcon />Novo grupo</button></div>
      </div>
      <p className="ng-explic">A ordem aqui é a ordem do menu de quem abre este ambiente. Para mudar um dashboard de grupo, abra o dashboard neste ambiente.</p>
      <div className="ng-lista">
        {ordenados.length === 0 && <p className="ng-explic">Nenhum grupo ainda.</p>}
        {ordenados.map((g, i) => (
          <div key={g.id} className="ng-linha">
            <span className="ng-amb-ini">{i + 1}</span>
            <span className="ng-amb-txt"><b>{g.nome}</b><small>{plural(g.dashboards.length, 'dashboard', 'dashboards')}</small></span>
            <button type="button" className="ng-ico" disabled={i === 0} onClick={() => mover(i, i - 1)} aria-label={`Subir ${g.nome}`}><ArrowUpIcon fontSize="small" /></button>
            <button type="button" className="ng-ico" disabled={i === ordenados.length - 1} onClick={() => mover(i, i + 1)} aria-label={`Descer ${g.nome}`}><ArrowDownIcon fontSize="small" /></button>
            <button type="button" className="ng-ico" onClick={() => abrir('renomear', g)} aria-label={`Renomear ${g.nome}`}><EditIcon fontSize="small" /></button>
            <button type="button" className="ng-ico perigo" disabled={g.dashboards.length > 0} onClick={() => abrir('excluir', g)}
                    aria-label={`Excluir ${g.nome}`} title={g.dashboards.length ? 'Só dá para excluir grupo vazio: mude os dashboards de grupo antes' : 'Excluir o grupo'}><CloseIcon fontSize="small" /></button>
          </div>
        ))}
      </div>

      <Janela aberto={janela?.tipo === 'novo' || janela?.tipo === 'renomear'} titulo={janela?.tipo === 'novo' ? 'Novo grupo' : 'Renomear grupo'} botao={janela?.tipo === 'novo' ? 'Criar grupo' : 'Salvar'}
              desabilitado={!nome.trim() || (janela?.tipo === 'renomear' && nome.trim() === janela.grupo?.nome)} salvando={salvando} erro={erro}
              aoConfirmar={confirmar} aoFechar={() => setJanela(null)}>
        <label className="nv-campo"><span>Nome no menu</span><input value={nome} maxLength={100} onChange={e => setNome(e.target.value)} placeholder="Ex.: Execução" /></label>
      </Janela>
      <Janela aberto={janela?.tipo === 'excluir'} titulo="Excluir grupo" botao="Excluir" perigo salvando={salvando} erro={erro} aoConfirmar={confirmar} aoFechar={() => setJanela(null)}>
        <p style={{ margin: 0 }}>Excluir o grupo <b>{janela?.grupo?.nome}</b> do menu deste ambiente? Ele está vazio.</p>
      </Janela>
    </div>
  )
}
